import React from 'react'
import classnames from 'classnames'
import deepEqual from 'react-fast-compare'
import { SegmentListContext } from './segment-list-context'

import {
    AndroidFilled,
    ApartmentOutlined,
    AppleFilled,
    ChromeFilled,
    DownOutlined,
    FolderOutlined,
    GlobalOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import { IOrgSegmentListState, SegmentListState } from './interfaces'
import { AppService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import { DeliveryChannel, SegmentSource, getDeliveryChannelLongName } from '@pushly/models/lib/enums'
import { AppState } from '../../stores/app'
import { simpleSort } from '../../_utils/utils'
import { FEAT_INLINE_SEG } from '../../constants'
import { hasFlag } from '../../core/flags/has-flag'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { Button, Dropdown, Flex, Input, Menu, Modal, Tooltip } from 'antd'
import { FilterBar, Select } from '@pushly/aqe/lib/components'
import type { FilterDefinition, ActiveFilter } from '@pushly/aqe/lib/components'

type FilterKey = keyof SegmentListState['filters']

interface ISegmentListHeaderProps {
    title?: string
    filterSize?: SizeType
    filtersAddonBefore?: React.ReactNode
    filtersAddonAfter?: React.ReactNode
    hideRefresh?: boolean
    hideRowSelection?: boolean
    hideFilters?: FilterKey | FilterKey[] | boolean
    filtersDisabled?: FilterKey | FilterKey[] | boolean
    onArchiveClick: () => void
    onAddDomains: (domainIds: number[]) => any
}

interface IState {
    status?: string[]
    domainIdsToAdd?: number[]
}

class SegmentListHeader extends React.Component<ISegmentListHeaderProps, IState> {
    public static contextType = SegmentListContext
    public context!: React.ContextType<typeof SegmentListContext>

    public state: IState = {}

    protected readonly appState: AppState
    protected readonly appService: AppService

    protected liveTypeTimeout: any

    public constructor(props: ISegmentListHeaderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public render() {
        const {
            levelDependenciesLoaded,
            refreshing,
            filters,
            onSearchClick,
            onFilterChange,
            onRefreshClick,
            refreshEnabled,
        } = this.context

        const allFilters: FilterKey[] = ['search', 'source', 'showAllSegments']
        const filterSize = this.props.filterSize ?? 'middle'

        let filtersHidden: FilterKey[] = []
        if (this.props.hideFilters === true) {
            filtersHidden = Array.from(allFilters)
        } else if (this.props.hideFilters) {
            filtersHidden = Array.isArray(this.props.hideFilters) ? this.props.hideFilters : [this.props.hideFilters]
        }

        let filtersDisabled: FilterKey[] = []
        if (this.props.filtersDisabled === true) {
            filtersDisabled = Array.from(allFilters)
        } else if (this.props.filtersDisabled) {
            filtersDisabled = Array.isArray(this.props.filtersDisabled)
                ? this.props.filtersDisabled
                : [this.props.filtersDisabled]
        }

        const hideAllFilters = filtersHidden.length === allFilters.length

        let hasInlineSegFeat = false
        if (this.context.level === 'domain' && this.context.domain) {
            hasInlineSegFeat = hasFlag(this.context.domain, FEAT_INLINE_SEG)
        }

        const isOrgLevel = this.context.level === 'org'

        const filterDefinitions: FilterDefinition[] = [
            {
                key: 'source',
                label: 'Source',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: SegmentSource.STANDARD, label: 'Standard' },
                    { value: SegmentSource.INLINE, label: 'Inline' },
                ],
                condition: () => !filtersHidden.includes('source') && hasInlineSegFeat,
            },
            {
                key: 'channels',
                label: 'Channels',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    {
                        value: DeliveryChannel.WEB,
                        label: getDeliveryChannelLongName(DeliveryChannel.WEB),
                        icon: <ChromeFilled />,
                    },
                    {
                        value: DeliveryChannel.NATIVE_IOS,
                        label: getDeliveryChannelLongName(DeliveryChannel.NATIVE_IOS),
                        icon: <AppleFilled />,
                    },
                    {
                        value: DeliveryChannel.NATIVE_ANDROID,
                        label: getDeliveryChannelLongName(DeliveryChannel.NATIVE_ANDROID),
                        icon: <AndroidFilled />,
                    },
                ],
                condition: () => !filtersHidden.includes('channels'),
            },
            {
                key: 'scope',
                label: 'Scope',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: 'multi-domain', label: 'Multi-Domain', icon: <ApartmentOutlined />, disabled: true },
                    { value: 'single-domain', label: 'Single-Domain', icon: <GlobalOutlined /> },
                ],
                condition: () => !filtersHidden.includes('showAllSegments') && isOrgLevel,
            },
        ]

        const filterBarValue: ActiveFilter[] = []
        if (hasInlineSegFeat && filters.source && filters.source.length > 0) {
            filterBarValue.push({ key: 'source', operator: 'in', value: filters.source })
        }
        if (!filtersHidden.includes('channels') && filters.channels && filters.channels.length > 0) {
            filterBarValue.push({ key: 'channels', operator: 'in', value: filters.channels })
        }
        // Scope filter: multi-domain is always selected, single-domain is optional
        if (isOrgLevel) {
            const scopeValue = filters.showAllSegments ? ['multi-domain', 'single-domain'] : ['multi-domain']
            filterBarValue.push({ key: 'scope', operator: 'in', value: scopeValue })
        }

        const handleFilterBarApply = (appliedFilters: ActiveFilter[]) => {
            const newSource = (appliedFilters.find((f) => f.key === 'source')?.value as SegmentSource[]) || []
            if (!deepEqual(newSource, filters.source ?? [])) {
                onFilterChange('source', newSource)
            }

            const newChannels = (appliedFilters.find((f) => f.key === 'channels')?.value as DeliveryChannel[]) || []
            if (!deepEqual(newChannels, filters.channels ?? [])) {
                onFilterChange('channels', newChannels)
            }

            // Handle scope filter - single-domain presence means showAllSegments is true
            const scopeFilter = appliedFilters.find((f) => f.key === 'scope')
            const scopeValues = (scopeFilter?.value as string[]) || ['multi-domain']
            const showSingleDomain = scopeValues.includes('single-domain')
            if (showSingleDomain !== filters.showAllSegments) {
                onFilterChange('showAllSegments', showSingleDomain)
            }
        }

        const totalSelected = this.context.selectedIds.length
        const bulkActionsEnabled = totalSelected > 0

        return (
            <Flex gap={8} align="center" justify="space-between" className={classnames('segment-list-header')}>
                <Flex>
                    <div
                        className={classnames('segment-list-title', {
                            hidden: !this.props.title,
                        })}
                    >
                        <span>{this.props.title}</span>
                    </div>

                    <Flex
                        gap={8}
                        flex={1}
                        className={classnames('segment-list-filters', {
                            hidden: hideAllFilters,
                        })}
                    >
                        {this.props.filtersAddonBefore}

                        {!filtersHidden.includes('search') && (
                            <Input.Search
                                className={classnames('segment-list-search')}
                                style={{ width: 300 }}
                                size={filterSize}
                                placeholder="Search by segment name"
                                disabled={filtersDisabled.includes('search')}
                                enterButton={true}
                                defaultValue={filters.search}
                                onSearch={onSearchClick}
                                onChange={(ev) => {
                                    clearTimeout(this.liveTypeTimeout)

                                    const value = ev.target.value
                                    this.liveTypeTimeout = setTimeout(() => {
                                        onFilterChange('search', value)
                                    }, 320)
                                }}
                            />
                        )}

                        <FilterBar
                            filterDefinitions={filterDefinitions}
                            value={filterBarValue}
                            onApply={handleFilterBarApply}
                            showInlineTags={false}
                            showInlineClearAll={false}
                            disabled={filtersDisabled.includes('source') && filtersDisabled.includes('channels')}
                            size={filterSize as 'small' | 'middle' | 'large'}
                        />

                        {this.props.filtersAddonAfter}
                    </Flex>
                </Flex>

                <Flex gap={8} flex={0} align="center">
                    {this.appState.abilityStore.can(AbilityAction.UPDATE, SubjectEntity.SEGMENT) && (
                        <div
                            className={classnames('segment-list-bulk-actions', {
                                hidden: this.props.hideRowSelection,
                            })}
                        >
                            {!this.props.hideRowSelection && (
                                <Dropdown
                                    disabled={!bulkActionsEnabled}
                                    trigger={['click']}
                                    popupRender={() => (
                                        <Menu onClick={this.handleBulkMenuClick}>
                                            {this.context.level === 'org' && (
                                                <Menu.Item key="action-add-domains" icon={<GlobalOutlined />}>
                                                    <span>Add Domains</span>
                                                </Menu.Item>
                                            )}
                                            <Menu.Item key="action-archive" icon={<FolderOutlined />}>
                                                <span>Archive</span>
                                            </Menu.Item>
                                        </Menu>
                                    )}
                                >
                                    <Button size="small" type={bulkActionsEnabled ? 'primary' : 'default'}>
                                        <span>Bulk Actions</span>
                                        <DownOutlined />
                                    </Button>
                                </Dropdown>
                            )}
                        </div>
                    )}

                    <div
                        className={classnames('segment-list-refresh', {
                            hidden: this.props.hideRefresh,
                        })}
                    >
                        {!this.props.hideRefresh && (
                            <Tooltip title="Refresh">
                                <Button
                                    className={classnames('segment-list-refresh-toggle')}
                                    aria-label="Refresh segments"
                                    disabled={!refreshEnabled}
                                    type="text"
                                    shape="round"
                                    icon={
                                        <ReloadOutlined
                                            className="text--size--large"
                                            spin={!levelDependenciesLoaded || refreshing}
                                        />
                                    }
                                    onClick={onRefreshClick}
                                />
                            </Tooltip>
                        )}
                    </div>
                </Flex>
            </Flex>
        )
    }

    protected handleBulkMenuClick = (e: any) => {
        switch (e.key) {
            case 'action-archive':
                this.confirmBulkArchive()
                break
            case 'action-add-domains':
                this.confirmBulkDomains()
                break
        }
    }

    protected confirmBulkArchive() {
        const checkedSegmentIds = Array.from(new Set(this.context.selectedIds))
        const totalSelected = checkedSegmentIds.length
        const itemText = totalSelected === 1 ? 'Segment' : 'Segments'

        Modal.confirm({
            title: 'Bulk Archive',
            okText: 'Yes, Archive',
            onOk: this.props.onArchiveClick,
            content: (
                <div>
                    Are you sure you want to archive <b>{totalSelected}</b> {itemText}?
                    <p>These segments will no longer be available to target during notification creation.</p>
                </div>
            ),
        })
    }

    protected confirmBulkDomains() {
        const context = this.context as IOrgSegmentListState
        const accessibleDomains =
            this.appState.currentUserDomains?.filter((d) => d.accountId?.toString() === context.org.id.toString()) ?? []

        const selectedSegments = context.dataSource.filter((s) => this.context.selectedIds.includes(s.getId()))

        // build map of domain ids by segment share
        const selectedDomainIdMap: { [index: number]: number[] } = {}
        selectedSegments.forEach((segment) => {
            segment.getDomainIds().forEach((domainId) => {
                selectedDomainIdMap[domainId] = selectedDomainIdMap[domainId] ?? []
                selectedDomainIdMap[domainId].push(segment.getId())
            })
        })

        // find domainIds that are shared across all selected segments
        // and remove them from the available domains
        const domainIdsToFilterOut = Object.keys(selectedDomainIdMap).filter((domainId) => {
            return selectedDomainIdMap[domainId].length === selectedSegments.length
        })
        const availableDomains = accessibleDomains.filter(
            (domain) => !domainIdsToFilterOut.includes(domain.id.toString()),
        )
        availableDomains.sort((a, b) => simpleSort(a.displayName, b.displayName))

        Modal.confirm({
            icon: <GlobalOutlined />,
            title: 'Add Domains',
            okText: 'Submit',
            onOk: () => {
                let resolver: any
                if (this.state.domainIdsToAdd) {
                    resolver = this.props.onAddDomains(this.state.domainIdsToAdd)
                }

                return Promise.resolve(resolver)
            },
            content: (
                <div>
                    <p>Choose one or more domains to add to the selected segments.</p>

                    <Select
                        className="segment-list-add-domains-select"
                        aria-label="Select domains to add"
                        popupClassName="segment-list-add-domains-dropdown"
                        mode="multiple"
                        placeholder="Select one or more domains"
                        optionFilterProp="title"
                        optionLabelProp="title"
                        filterOption={true}
                        onChange={(domainIdsToAdd) => this.setState({ domainIdsToAdd })}
                        options={availableDomains.map((domain) => ({
                            value: domain.id,
                            label: domain.displayName,
                            title: domain.displayName,
                        }))}
                    />
                </div>
            ),
        })
    }
}

export default SegmentListHeader
